import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Config } from '../models/config.model';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly httpClient = inject(HttpClient);

  private readonly urlBase: string = 'api/config';
  private isLoaded = false;

  async loadConfiguration(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getConfiguration().subscribe({
        next: (config: Config[]) => {
          const configObject = this.createConfigObject(config);
          sessionStorage.setItem('config', JSON.stringify(configObject));
          this.applyConfiguration(configObject);
          this.isLoaded = true;
          console.log('Configuration loaded from server');
          resolve();
        },
        error: (err) => reject(new Error(err)),
      });
    });
  }

  get contractorHolidayReimbursementVisible(): boolean {
    return this.getBooleanConfigValue('contractor.holiday.reimbursement.visible');
  }

  get conferenceRoomBookingVisible(): boolean {
    return this.getBooleanConfigValue('conference.room.booking.visible');
  }

  get probationReviewVisible(): boolean {
    return this.getBooleanConfigValue('review.cycle.probation.visible');
  }

  get travelRequestsAdminVisible(): boolean {
    return this.getBooleanConfigValue('travel.requests.admin.visible');
  }

  private getBooleanConfigValue(key: string): boolean {
    return this.getConfigValue(key) === 'true';
  }

  private getConfiguration(): Observable<Config[]> {
    return this.httpClient.get<Config[]>(this.urlBase).pipe(
      tap((config: Config[]) => {
        const configObject = this.createConfigObject(config);
        sessionStorage.setItem('config', JSON.stringify(configObject));
      }),
    );
  }

  private createConfigObject(config: Config[]): { [key: string]: string } {
    return config.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {});
  }

  private applyConfiguration(config: { [key: string]: string }) {
    Object.keys(config).forEach((key) => {
      (this as any)[key] = config[key];
    });
  }

  private getConfigValue(key: string): string {
    if (!this.isLoaded) {
      const cachedConfig = sessionStorage.getItem('config');
      if (!cachedConfig) {
        throw new Error('Configuration not found in session storage');
      }
      const configObject = JSON.parse(cachedConfig);
      this.applyConfiguration(configObject);
      this.isLoaded = true;
    }
    return (this as any)[key];
  }
}
